
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useTrial } from "@/hooks/useTrial";
import { useCustomOffer } from "@/hooks/useCustomOffer";
import { useBrandManager } from "@/hooks/useBrandManager";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { LogOut, User, Settings, UserCog, HardHat, CheckSquare, Plus, FileText, Moon, Sun, CreditCard, ExternalLink, Gift, Sparkles, Sunrise } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuickAdd } from "@/contexts/QuickAddContext";
import { usePersistedTheme } from "@/hooks/usePersistedTheme";
import { useGrowthStandupDialog } from "@/contexts/GrowthStandupDialogContext";
import { format } from "date-fns";

  const UserMenu = () => {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { setIsQuickAddOpen } = useQuickAdd();
    const { openDayStart, openDayEnd } = useGrowthStandupDialog();
    const { theme, setTheme, isLoading: themeLoading } = usePersistedTheme();
    const { subscribed, subscription_tier, subscription_end, openCustomerPortal, isLoading: subLoading } = useSubscription();
    const { isOnTrial, trialEnd } = useTrial();
    const { activeOffer, isLoading: offerLoading } = useCustomOffer();
    const { currentBrandId } = useBrandManager();
  
    const handleSignOut = async () => {
      if (isLoading) return; // Prevent multiple concurrent sign out attempts
      
      setIsLoading(true);
      try {
        await signOut();
        console.log('✅ [UserMenu] Sign out successful, determining redirect path');
  
        // If we know the current brand, try to use brand-specific student auth path
        if (currentBrandId) {
          try {
            const { data, error } = await (supabase.from as any)('os_lms_paths')
              .select('student_auth_path')
              .eq('brand_id', currentBrandId)
              .maybeSingle();
  
            if (!error && data?.student_auth_path) {
              const authPath = data.student_auth_path as string;
              const separator = authPath.includes('?') ? '&' : '?';
              navigate(`${authPath}${separator}brand_id=${currentBrandId}`);
              return;
            }
          } catch (pathError) {
            console.error('❌ [UserMenu] Error resolving brand-specific auth path, falling back to /auth:', pathError);
          }
        }
  
        // Fallback: redirect to default app auth page
        navigate('/auth');
      } catch (error) {
        console.error("❌ [UserMenu] Sign out error:", error);
        // Even if sign out fails, still redirect to auth page
        // since local state has been cleared
        navigate('/auth');
      } finally {
        setIsLoading(false);
      }
    };

  if (!user) return null;

  const userInitials = user.user_metadata?.full_name 
    ? user.user_metadata.full_name.split(' ').map((n: string) => n[0]).join('').toUpperCase()
    : user.email?.charAt(0).toUpperCase() || 'U';

  // Determine plan display
  const getPlanDisplay = () => {
    if (subscribed && subscription_tier) {
      return {
        name: `${subscription_tier.charAt(0).toUpperCase()}${subscription_tier.slice(1)} Plan`,
        status: 'Active',
        statusVariant: 'default' as const,
        nextBilling: subscription_end ? format(new Date(subscription_end), 'MMM dd, yyyy') : null,
      };
    }
    if (isOnTrial) {
      return {
        name: 'Trial',
        status: 'Trial',
        statusVariant: 'secondary' as const,
        nextBilling: trialEnd ? format(new Date(trialEnd), 'MMM dd, yyyy') : null,
      };
    }
    return {
      name: 'Free',
      status: 'Free',
      statusVariant: 'outline' as const,
      nextBilling: null,
    };
  };

  const planInfo = getPlanDisplay();

  const handleManageSubscription = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await openCustomerPortal();
  };

  const handleManagePlans = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/plans-and-billing');
  };

  const handleAcceptOffer = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeOffer) {
      navigate(`/offer/${activeOffer.offer_code}`);
    }
  };

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.user_metadata?.avatar_url} alt="User avatar" />
            <AvatarFallback>{userInitials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {user.user_metadata?.full_name || 'User'}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2 py-2 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span className="text-sm">{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
            </div>
            <Switch
              checked={theme === "dark"}
              onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
              disabled={themeLoading}
            />
          </div>
          <div className="flex gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 text-[10px] px-2"
              onClick={() => {
                setDropdownOpen(false);
                setTimeout(() => openDayStart(), 100);
              }}
            >
              <Sunrise className="h-3 w-3 mr-0.5" />
              Day Start
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 text-[10px] px-2"
              onClick={() => {
                setDropdownOpen(false);
                setTimeout(() => openDayEnd(), 100);
              }}
            >
              <Moon className="h-3 w-3 mr-0.5" />
              Day End
            </Button>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/my-tasks')} className="text-sm">
          <CheckSquare className="mr-2 h-4 w-4" />
          <span>My Tasks</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/tasks-monitoring')} className="text-sm">
          <HardHat className="mr-2 h-4 w-4" />
          <span>My Current Tasks</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/user-settings')} className="text-sm">
          <UserCog className="mr-2 h-4 w-4" />
          <span>User Settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/os-release-notes')} className="text-sm">
          <FileText className="mr-2 h-4 w-4" />
          <span>Latest Release Notes</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
          Across Brands
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => navigate('/my-tasks-all-brands')} className="text-sm">
          <CheckSquare className="mr-2 h-4 w-4" />
          <span>My Tasks</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/tasks-monitoring-all-brands')} className="text-sm">
          <HardHat className="mr-2 h-4 w-4" />
          <span>My Current Tasks</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/my-overall-dashboard')} className="text-sm">
          <CheckSquare className="mr-2 h-4 w-4" />
          <span>Overall View on Tasks</span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Plans & Credits Section */}
        <div className="px-2 py-3 space-y-2">
          {/* Custom Offer Available */}
          {activeOffer && !offerLoading && (
            <div className="mb-3 pb-3 border-b">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-600">Special Offer Available!</span>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-medium">
                  {activeOffer.base_plan} Plan
                  {Object.values(activeOffer.selected_addons).filter(Boolean).length > 0 && (
                    <span className="text-xs text-muted-foreground ml-1">
                      + {Object.values(activeOffer.selected_addons).filter(Boolean).length} addon{Object.values(activeOffer.selected_addons).filter(Boolean).length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  <span className="line-through">${activeOffer.calculated_price}</span>
                  <span className="text-green-600 dark:text-green-400 font-semibold ml-2">
                    ${activeOffer.final_price}/{activeOffer.billing_cycle === 'monthly' ? 'mo' : 'yr'}
                  </span>
                  <span className="text-green-600 dark:text-green-400 ml-1">
                    ({activeOffer.discount_percentage}% off)
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Expires: {format(new Date(activeOffer.expiration_date), 'MMM dd, yyyy')}
                </div>
                <Button 
                  size="sm" 
                  className="w-full h-8 text-xs mt-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  onClick={handleAcceptOffer}
                >
                  <Sparkles className="mr-1 h-3 w-3" />
                  Accept Offer
                </Button>
              </div>
            </div>
          )}

          {/* Current Plan */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Current Plan</span>
            <Badge variant={planInfo.statusVariant} className="text-xs">
              {subscribed && subscription_tier ? subscription_tier.charAt(0).toUpperCase() + subscription_tier.slice(1) : planInfo.status}
            </Badge>
          </div>
          {planInfo.nextBilling && (
            <div className="text-xs text-muted-foreground">
              Next billing: {planInfo.nextBilling}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full h-8 text-xs"
              onClick={handleManagePlans}
            >
              Manage Plans & Billing
            </Button>
          </div>
        </div>
        
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} disabled={isLoading} className="text-sm">
          <LogOut className="mr-2 h-4 w-4" />
          <span>{isLoading ? 'Signing out...' : 'Sign out'}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
